import React, { FC } from 'react';
import styled from 'styled-components';
import {
    SkeletonBlock,
    SkeletonDivider,
    SkeletonFooterBar,
    SkeletonPageHeader,
    SkeletonPageShell,
    SkeletonRow,
    SkeletonStack,
    SkeletonSurface
} from 'src/components/widgets/skeleton';

const ProfanitySkeletonShell = styled(SkeletonPageShell).attrs({ $padding: '20px 0 92px' })``;

const ProfanitySkeletonHeader = styled(SkeletonPageHeader)``;

const ProfanityCardsGridSkeleton = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 18px;
    min-width: 0;

    @media (max-width: 900px) {
        grid-template-columns: minmax(0, 1fr);
    }
`;

const ProfanityCardSkeleton = styled.div`
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 8px;
    background: #ffffff;
    padding: 22px;
    min-height: 150px;
    min-width: 0;
`;

const ProfanityTableSkeleton = styled.div`
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 8px;
    background: #ffffff;
    overflow: hidden;
    min-width: 0;
`;

const ProfanityTableRowSkeleton = styled.div`
    display: grid;
    grid-template-columns: minmax(180px, 1fr) 180px;
    gap: 18px;
    align-items: center;
    min-height: 58px;
    padding: 14px 18px;
    border-bottom: 1px solid ${(props) => props.theme.skeleton.backgroundColor};
    min-width: 0;

    &:last-child {
        border-bottom: 0;
    }

    @media (max-width: 700px) {
        grid-template-columns: minmax(0, 1fr) 72px;
    }
`;

const ProfanitySectionSkeleton = styled(SkeletonSurface).attrs({
    $padding: '24px',
    $overflow: 'visible'
})``;

const ProfanityFieldsGridSkeleton = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
    min-width: 0;

    @media (max-width: 760px) {
        grid-template-columns: minmax(0, 1fr);
    }
`;

const ProfanityIndentedSkeleton = styled.div`
    padding-left: 52px;
    min-width: 0;

    @media (max-width: 700px) {
        padding-left: 0;
    }
`;

const ProfanityCategoryRowSkeleton = styled.div`
    display: grid;
    grid-template-columns: 24px minmax(140px, 1fr) minmax(160px, 260px);
    align-items: center;
    gap: 14px;
    min-width: 0;

    @media (max-width: 700px) {
        grid-template-columns: 24px minmax(0, 1fr);

        > :last-child {
            grid-column: 2;
            width: 100%;
        }
    }
`;

const ProfanityLanguageGridSkeleton = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(120px, 1fr));
    gap: 12px 18px;
    min-width: 0;

    @media (max-width: 900px) {
        grid-template-columns: repeat(2, minmax(120px, 1fr));
    }

    @media (max-width: 560px) {
        grid-template-columns: minmax(0, 1fr);
    }
`;

const ProfanityFooterSkeleton = styled(SkeletonFooterBar).attrs({
    $position: 'static',
    $padding: '16px 0 0'
})``;

export const ProfanityFilterMenuPageSkeleton: FC = () => (
    <ProfanitySkeletonShell aria-hidden='true'>
        <ProfanitySkeletonHeader>
            <SkeletonStack $gap='8px' $flex='1'>
                <SkeletonBlock $width='min(340px, 100%)' $height='26px' />
                <SkeletonBlock $width='min(460px, 100%)' $height='13px' />
            </SkeletonStack>
        </ProfanitySkeletonHeader>
        <ProfanityCardsGridSkeleton>
            {Array.from({ length: 3 }).map((_, index) => (
                <ProfanityCardSkeleton key={index}>
                    <SkeletonStack $gap='14px'>
                        <SkeletonBlock $width='44px' $height='44px' $radius='8px' />
                        <SkeletonBlock $width={index === 0 ? '160px' : '130px'} $height='18px' />
                        <SkeletonBlock $width='92%' $height='12px' />
                        <SkeletonBlock $width='70%' $height='12px' />
                    </SkeletonStack>
                </ProfanityCardSkeleton>
            ))}
        </ProfanityCardsGridSkeleton>
    </ProfanitySkeletonShell>
);

export const ProfanityFilterRulesPageSkeleton: FC = () => (
    <ProfanitySkeletonShell aria-hidden='true'>
        <ProfanitySkeletonHeader>
            <SkeletonStack $gap='8px' $flex='1'>
                <SkeletonBlock $width='min(340px, 100%)' $height='26px' />
                <SkeletonBlock $width='min(520px, 100%)' $height='13px' />
            </SkeletonStack>
            <SkeletonBlock $width='86px' $height='36px' $radius='6px' />
        </ProfanitySkeletonHeader>
        <ProfanityTableSkeleton>
            {Array.from({ length: 7 }).map((_, index) => (
                <ProfanityTableRowSkeleton key={index}>
                    <SkeletonRow $gap='10px'>
                        <SkeletonBlock $width={index % 2 === 0 ? '220px' : '160px'} $height='15px' />
                        <SkeletonBlock $width='110px' $height='22px' $radius='11px' />
                    </SkeletonRow>
                    <SkeletonRow $gap='8px' $justify='flex-end'>
                        <SkeletonBlock $width='28px' $height='28px' $radius='4px' />
                        <SkeletonBlock $width='28px' $height='28px' $radius='4px' />
                    </SkeletonRow>
                </ProfanityTableRowSkeleton>
            ))}
        </ProfanityTableSkeleton>
    </ProfanitySkeletonShell>
);

export const ProfanityFilterRulePageSkeleton: FC = () => (
    <ProfanitySkeletonShell aria-hidden='true'>
        <ProfanitySkeletonHeader>
            <SkeletonStack $gap='8px' $flex='1'>
                <SkeletonBlock $width='min(300px, 100%)' $height='26px' />
                <SkeletonBlock $width='min(450px, 100%)' $height='13px' />
            </SkeletonStack>
        </ProfanitySkeletonHeader>
        <SkeletonStack $gap='18px'>
            <SkeletonBlock $height='64px' $radius='8px' />
            <ProfanitySectionSkeleton>
                <SkeletonStack $gap='16px'>
                    <SkeletonBlock $width='92px' $height='20px' />
                    <SkeletonStack $gap='7px' $width='min(520px, 100%)'>
                        <SkeletonBlock $width='82px' $height='12px' />
                        <SkeletonBlock $height='40px' $radius='6px' />
                    </SkeletonStack>
                </SkeletonStack>
            </ProfanitySectionSkeleton>
            <ProfanitySectionSkeleton>
                <SkeletonStack $gap='18px'>
                    <SkeletonBlock $width='180px' $height='20px' />
                    <SkeletonRow $gap='12px'>
                        <SkeletonBlock $width='40px' $height='22px' $radius='11px' />
                        <SkeletonBlock $width='72px' $height='14px' />
                    </SkeletonRow>
                    <ProfanityIndentedSkeleton>
                        <SkeletonRow $gap='10px'>
                            <SkeletonBlock $width='18px' $height='18px' $radius='4px' />
                            <SkeletonBlock $width='min(270px, calc(100% - 28px))' $height='13px' />
                        </SkeletonRow>
                    </ProfanityIndentedSkeleton>
                    <SkeletonDivider />
                    <SkeletonRow $gap='12px'>
                        <SkeletonBlock $width='40px' $height='22px' $radius='11px' />
                        <SkeletonBlock $width='112px' $height='14px' />
                    </SkeletonRow>
                    <SkeletonStack $gap='14px'>
                        {Array.from({ length: 5 }).map((_, index) => (
                            <ProfanityCategoryRowSkeleton key={index}>
                                <SkeletonBlock $width='18px' $height='18px' $radius='4px' />
                                <SkeletonBlock $width={index % 2 === 0 ? '180px' : '140px'} $height='13px' />
                                <SkeletonBlock $height='36px' $radius='6px' />
                            </ProfanityCategoryRowSkeleton>
                        ))}
                    </SkeletonStack>
                </SkeletonStack>
            </ProfanitySectionSkeleton>
            <ProfanitySectionSkeleton>
                <SkeletonStack $gap='18px'>
                    <SkeletonStack $gap='8px'>
                        <SkeletonBlock $width='160px' $height='20px' />
                        <SkeletonBlock $width='min(620px, 100%)' $height='13px' />
                    </SkeletonStack>
                    <SkeletonRow $gap='10px'>
                        <SkeletonBlock $width='18px' $height='18px' $radius='4px' />
                        <SkeletonBlock $width='132px' $height='13px' />
                    </SkeletonRow>
                    <ProfanityLanguageGridSkeleton>
                        {Array.from({ length: 12 }).map((_, index) => (
                            <SkeletonRow key={index} $gap='10px'>
                                <SkeletonBlock $width='18px' $height='18px' $radius='4px' />
                                <SkeletonBlock $width={index % 3 === 0 ? '86px' : '68px'} $height='13px' />
                            </SkeletonRow>
                        ))}
                    </ProfanityLanguageGridSkeleton>
                </SkeletonStack>
            </ProfanitySectionSkeleton>
            <ProfanityFooterSkeleton>
                <SkeletonBlock $width='110px' $height='36px' $radius='6px' />
                <SkeletonBlock $width='88px' $height='36px' $radius='6px' />
            </ProfanityFooterSkeleton>
        </SkeletonStack>
    </ProfanitySkeletonShell>
);

export const ProfanityFilterAnalyticsPageSkeleton: FC = () => (
    <ProfanitySkeletonShell aria-hidden='true'>
        <ProfanitySkeletonHeader>
            <SkeletonStack $gap='8px' $flex='1'>
                <SkeletonBlock $width='min(280px, 100%)' $height='26px' />
                <SkeletonBlock $width='min(420px, 100%)' $height='13px' />
            </SkeletonStack>
        </ProfanitySkeletonHeader>
        <ProfanityFieldsGridSkeleton>
            {Array.from({ length: 4 }).map((_, index) => (
                <ProfanitySectionSkeleton key={index}>
                    <SkeletonStack $gap='12px'>
                        <SkeletonBlock $width='42%' $height='13px' />
                        <SkeletonBlock $width={index % 2 === 0 ? '80px' : '110px'} $height='28px' />
                    </SkeletonStack>
                </ProfanitySectionSkeleton>
            ))}
        </ProfanityFieldsGridSkeleton>
    </ProfanitySkeletonShell>
);
